'use client';

import {
  RefObject,
  SVGProps,
  createContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/cn';
import { motion, MotionConfig, useReducedMotion } from 'motion/react';

import { Button } from '@/components/Button';
import { Container } from '@/components/Container';
import { Footer } from '@/components/Footer';
import { GridPattern } from '@/components/GridPattern';
import { Logo, Logomark, SaplingLogo } from '@/components/Logo';
import { Addresses } from '@/components/Addresses';
import { SocialMedia } from '@/components/SocialMedia';
import { MainNav } from './MainNav';

const RootLayoutContext = createContext({});

type RootLayoutNewProps = {
  children: React.ReactNode;
  transparent?: boolean;
};

export function RootLayoutNew({
  children,
  transparent = false,
}: RootLayoutNewProps) {
  let pathname = usePathname();
  let [logoHovered, setLogoHovered] = useState(false);

  return (
    <RootLayoutContext.Provider value={{ logoHovered, setLogoHovered }}>
      <RootLayoutInner invert={transparent} key={pathname}>
        {children}
      </RootLayoutInner>
    </RootLayoutContext.Provider>
  );
}

type RootLayoutInnerProps = {
  children: React.ReactNode;
  invert?: boolean;
};

function RootLayoutInner({ children, invert }: RootLayoutInnerProps) {
  let panelId = useId();
  let [expanded, setExpanded] = useState(false);
  let openRef = useRef<HTMLButtonElement | null>(null);
  let closeRef = useRef<HTMLButtonElement | null>(null);
  let navRef = useRef<HTMLDivElement | null>(null);
  let shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        (event.target as HTMLElement | null)?.closest('a')?.href ===
        window.location.href
      ) {
        setExpanded(false);
      }
    }

    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('click', onClick);
    };
  }, []);

  return (
    <MotionConfig transition={shouldReduceMotion ? { duration: 0 } : undefined}>
      <header>
        <div
          className="absolute left-0 right-0 top-2 z-40"
          aria-hidden={expanded ? 'true' : undefined}
          inert={expanded ? true : undefined}
        >
          <Header
            panelId={panelId}
            icon={MenuIcon}
            toggleRef={openRef}
            expanded={expanded}
            invert={invert}
            onToggle={() => {
              setExpanded((expanded) => !expanded);
              window.setTimeout(() =>
                closeRef.current?.focus({ preventScroll: true }),
              );
            }}
          />
        </div>

        <motion.div
          layout
          id={panelId}
          style={{ height: expanded ? 'auto' : '0.5rem' }}
          className="relative z-50 overflow-hidden bg-foreground pt-2"
          aria-hidden={expanded ? undefined : 'true'}
          inert={expanded ? undefined : true}
        >
          <motion.div layout className="bg-neutral-800">
            <div ref={navRef} className="bg-foreground pb-16">
              <Header
                invert
                panelId={panelId}
                icon={XIcon}
                toggleRef={closeRef}
                expanded={expanded}
                onToggle={() => {
                  setExpanded((expanded) => !expanded);
                  window.setTimeout(() =>
                    openRef.current?.focus({ preventScroll: true }),
                  );
                }}
              />
            </div>
            <Navigation />
            <div className="relative bg-neutral-950 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-neutral-800">
              <Container>
                <div className="grid grid-cols-1 gap-y-10 pb-16 pt-10 sm:grid-cols-2 sm:pt-16">
                  <div>
                    {/* <h2 className="font-display text-base font-semibold text-white">
                      Kontaktuppgifter
                    </h2> */}
                    <Addresses
                      invert
                      className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2"
                    />
                  </div>
                  <div className="sm:border-l sm:border-transparent sm:pl-16">
                    <h2 className="font-display text-base font-semibold text-white">
                      Följ oss
                    </h2>
                    <SocialMedia className="mt-6" invert />
                  </div>
                </div>
              </Container>
            </div>
          </motion.div>
        </motion.div>
      </header>

      <motion.div
        layout
        style={{ borderTopLeftRadius: 40, borderTopRightRadius: 40 }}
        className="relative flex flex-auto overflow-hidden bg-black"
      >
        <motion.div
          layout
          className="relative isolate flex w-full flex-col bg-transparent"
        >
          {/* <GridPattern
            className="absolute inset-x-0 -top-14 -z-10 h-[1000px] w-full fill-neutral-50 stroke-neutral-950/5 [mask-image:linear-gradient(to_bottom_left,white_40%,transparent_50%)]"
            yOffset={-96}
            interactive
          /> */}

          <div className="w-full flex-auto">{children}</div>
        </motion.div>
      </motion.div>
    </MotionConfig>
  );
}

function XIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m5.636 4.223 14.142 14.142-1.414 1.414L4.222 5.637z" />
      <path d="M4.222 18.363 18.364 4.22l1.414 1.414L5.636 19.777z" />
    </svg>
  );
}

function MenuIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M2 6h20v2H2zM2 16h20v2H2z" />
    </svg>
  );
}

type HeaderProps = {
  panelId: string;
  invert?: boolean;
  icon: React.ComponentType<SVGProps<SVGSVGElement>>;
  expanded: boolean;
  onToggle: () => void;
  toggleRef: RefObject<HTMLButtonElement | null>;
};

function Header({
  panelId,
  invert = false,
  icon: Icon,
  expanded,
  onToggle,
  toggleRef,
}: HeaderProps) {
  // let { logoHovered, setLogoHovered } = useContext(RootLayoutContext)

  return (
    <Container>
      <div className="flex items-center justify-between pt-14">
        <Link
          href="/"
          aria-label="Home"
          // onMouseEnter={() => setLogoHovered(true)}
          // onMouseLeave={() => setLogoHovered(false)}
        >
          {/* <Logomark
            className="h-8 sm:hidden"
            invert={invert}
            filled={logoHovered}
          />
          <Logo
            className="hidden h-8 sm:block"
            invert={invert}
            filled={logoHovered}
          /> */}
          <SaplingLogo
            // className="hidden h-8 sm:block"
            className="h-10 sm:h-16"
            invert={invert}
            // filled={logoHovered}
          />
        </Link>
        <div className="flex items-center gap-x-8">
          {!expanded ? <MainNav transparent={invert} /> : null}
          <button
            ref={toggleRef}
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={`${panelId}-button`}
            className={cn(
              'group -m-2.5 rounded-full p-2.5 transition',
              invert ? 'hover:bg-white/10' : 'hover:bg-neutral-950/10',
            )}
            aria-label="Toggle navigation"
          >
            <Icon
              className={cn(
                'h-6 w-6',
                invert
                  ? 'fill-white group-hover:fill-neutral-200'
                  : 'fill-neutral-950 group-hover:fill-neutral-700',
              )}
            />
          </button>
        </div>
      </div>
    </Container>
  );
}

function NavigationRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="even:mt-px sm:bg-neutral-950">
      <Container className={''}>
        <div className="grid grid-cols-1 sm:grid-cols-2">{children}</div>
      </Container>
    </div>
  );
}

function NavigationItem({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className="group relative isolate -mx-6 bg-neutral-950 px-6 py-10 even:mt-px sm:mx-0 sm:px-0 sm:py-16 sm:odd:pr-16 sm:even:mt-0 sm:even:border-l sm:even:border-neutral-800 sm:even:pl-16"
    >
      {children}
      <span className="absolute inset-y-0 -z-10 w-screen bg-neutral-900 opacity-0 transition group-odd:right-0 group-even:left-0 group-hover:opacity-100" />
    </Link>
  );
}

function Navigation() {
  return (
    <nav className="mt-px font-display text-lg font-medium tracking-tight text-white">
      <NavigationRow>
        <NavigationItem href="/skyddat-boende">Skyddat boende</NavigationItem>
        <NavigationItem href="/jour-och-familjehem">
          Jour- och familjehem
        </NavigationItem>
        <NavigationItem href="/traningslagenhet">
          Träningslägenhet
        </NavigationItem>
        <NavigationItem href="/oppenvard">Öppenvård</NavigationItem>
      </NavigationRow>

      <NavigationRow>
        <NavigationItem href="/for-socialtjanst">
          För Socialtjänst
        </NavigationItem>
        <NavigationItem href="/om-oss">Om oss</NavigationItem>
      </NavigationRow>
    </nav>
  );
}
